import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaFigma, FaGithub, FaJs, FaReact, FaWhatsapp } from 'react-icons/fa';
import { SiAdobephotoshop, SiOpenai } from 'react-icons/si';

const Section = styled.section`
  position: relative;
  padding: 130px 24px 120px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 96px 18px 92px;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
`;

const CtaPanel = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 500px;
  justify-content: center;
  isolation: isolate;

  @media (max-width: 768px) {
    min-height: 520px;
  }
`;

const SoftGlow = styled.div`
  position: absolute;
  inset: 12% 14%;
  background:
    radial-gradient(circle at 50% 40%, rgba(0, 149, 159, 0.08), transparent 35%),
    radial-gradient(circle at 44% 55%, rgba(20, 20, 20, 0.05), transparent 32%);
  filter: blur(16px);
  z-index: -2;
`;

const Avatar = styled(motion.img)`
  width: 92px;
  height: 92px;
  border-radius: 999px;
  object-fit: cover;
  object-position: center top;
  margin-bottom: 26px;
  box-shadow: 0 18px 45px rgba(10, 10, 10, 0.12);

  @media (max-width: 768px) {
    width: 78px;
    height: 78px;
    margin-bottom: 22px;
  }
`;

const Title = styled(motion.h2)`
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 500;
  font-size: clamp(3rem, 7.2vw, 5.8rem);
  line-height: 0.94;
  letter-spacing: -0.085em;
  color: ${({ theme }) => theme.colors.ink};
  margin: 0;
  max-width: 820px;
`;

const Sub = styled(motion.p)`
  margin: 30px 0 0;
  font-size: clamp(1rem, 1.7vw, 1.18rem);
  color: ${({ theme }) => theme.colors.ink};
  max-width: 650px;
  line-height: 1.35;
  letter-spacing: -0.03em;
`;

const Links = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-top: 32px;
  flex-wrap: wrap;
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 46px;
  padding: 0 24px;
  border-radius: 999px;
  font-size: 0.96rem;
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 1;
  color: #FFFFFF;
  background: ${({ $variant }) => ($variant === 'whatsapp' ? '#25D366' : '#0A0A0A')};
  box-shadow: ${({ $variant }) =>
    $variant === 'whatsapp'
      ? '0 16px 30px rgba(37, 211, 102, 0.22)'
      : '0 16px 30px rgba(10, 10, 10, 0.16)'};
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
  }

  .material-symbols-rounded {
    font-size: 18px;
  }
`;

const WhatsAppIcon = styled.span`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
`;

const FloatingBadge = styled(motion.div)`
  position: absolute;
  ${({ $top }) => $top && `top: ${$top};`}
  ${({ $right }) => $right && `right: ${$right};`}
  ${({ $bottom }) => $bottom && `bottom: ${$bottom};`}
  ${({ $left }) => $left && `left: ${$left};`}
  width: ${({ $size }) => $size || '54px'};
  height: ${({ $size }) => $size || '54px'};
  border-radius: ${({ $shape }) => ($shape === 'diamond' ? '10px' : '14px')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ $fontSize }) => $fontSize || '1.15rem'};
  font-weight: 800;
  letter-spacing: -0.06em;
  color: ${({ $color }) => $color || '#fff'};
  background: ${({ $bg }) => $bg || '#0A0A0A'};
  box-shadow: 0 16px 35px rgba(10, 10, 10, 0.12);
  transform: ${({ $shape }) => ($shape === 'diamond' ? 'rotate(45deg)' : 'none')};

  span,
  svg {
    transform: ${({ $shape }) => ($shape === 'diamond' ? 'rotate(-45deg)' : 'none')};
  }

  svg {
    width: 58%;
    height: 58%;
    display: block;
  }

  @media (max-width: 768px) {
    opacity: 0.82;
    transform: ${({ $shape }) => ($shape === 'diamond' ? 'rotate(45deg) scale(0.78)' : 'scale(0.78)')};
  }

  @media (max-width: 560px) {
    ${({ $hideMobile }) => $hideMobile && 'display: none;'}
  }
`;

const Contact = () => {
  return (
    <Section id="contact">
      <Container>
        <CtaPanel
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: '-80px' }}
        >
          <SoftGlow />
          <FloatingBadge $top="16%" $left="8%" $bg="#0A0A0A" $color="#F7DF1E" $size="42px" aria-label="JavaScript">
            <FaJs />
          </FloatingBadge>
          <FloatingBadge $top="28%" $right="10%" $bg="#3B0764" $color="#FF7ADF" $size="46px" aria-label="AI">
            <SiOpenai />
          </FloatingBadge>
          <FloatingBadge $bottom="20%" $left="16%" $bg="#001E36" $color="#31A8FF" $size="48px" $hideMobile aria-label="Adobe Photoshop">
            <SiAdobephotoshop />
          </FloatingBadge>
          <FloatingBadge $bottom="15%" $right="18%" $bg="#FFFFFF" $color="#0ACF83" $size="48px" $hideMobile aria-label="Figma">
            <FaFigma />
          </FloatingBadge>
          <FloatingBadge $top="17%" $right="28%" $bg="#FFFFFF" $color="#0ACF83" $size="34px" $hideMobile aria-label="Figma">
            <FaFigma />
          </FloatingBadge>
          <FloatingBadge $top="34%" $left="26%" $bg="linear-gradient(135deg, #61DAFB, #149ECA)" $color="#08252F" $shape="diamond" $size="42px" $hideMobile aria-label="React">
            <FaReact />
          </FloatingBadge>

          <Avatar
            src="/avatar-maker.svg"
            alt="Jabez Llave avatar"
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            viewport={{ once: true }}
          />
          <Title
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Are you looking for the perfect build?
          </Title>
          <Sub
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Then you’re in the right place. Get clean interfaces, reliable
            systems, and AI-powered products built with care. Just reach out and
            let me know!
          </Sub>
          <Links
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.18 }}
            viewport={{ once: true }}
          >
            <ActionButton href="mailto:jabezllave@gmail.com">Email me</ActionButton>
            <ActionButton
              href="https://wa.me/639636424793"
              target="_blank"
              rel="noopener noreferrer"
              $variant="whatsapp"
            >
              <WhatsAppIcon aria-hidden="true"><FaWhatsapp /></WhatsAppIcon>
              Chat on WhatsApp
            </ActionButton>
            <ActionButton
              href="https://github.com/jabezllave"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <WhatsAppIcon aria-hidden="true"><FaGithub /></WhatsAppIcon>
              GitHub
            </ActionButton>
          </Links>
        </CtaPanel>
      </Container>
    </Section>
  );
};

export default Contact;
